import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { DAY_NAMES, formatDateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { ExternalSystemsPanel } from './ExternalSystems';

interface SettingSpec {
  key: string;
  label: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'weekday';
  options?: string[];
  hint?: string;
}

/**
 * §30 — sys's settings: finance, payroll and client numbering. The rest of §30
 * (company, SLA, tickets, shifts, security) is changed in the admin; the API
 * refuses a sys session that tries, and the other way round.
 */
const GROUPS: Array<{ id: string; title: TranslationKey; note?: TranslationKey; settings: SettingSpec[] }> = [
  {
    id: 'finance',
    title: 'settings.finance',
    settings: [
      {
        key: 'finance.baseCurrency',
        label: 'Base currency',
        type: 'text',
        hint:
          'CR-001 §6 — every payment is converted into this and every dashboard total is reported in it. ' +
          'Changing it does not re-convert payments already recorded: their rate is a historical snapshot (§27).',
      },
      {
        key: 'finance.allowInvoiceCurrencyOverride',
        label: 'Allow an invoice currency other than the client’s',
        type: 'boolean',
        hint: 'When off, only a user with finance.exchange_rate.edit may deviate from the client currency (§15).',
      },
      { key: 'finance.invoiceNumberPrefix', label: 'Invoice number prefix', type: 'text' },
      { key: 'finance.paymentNumberPrefix', label: 'Payment number prefix', type: 'text' },
      { key: 'finance.contractNumberPrefix', label: 'Contract number prefix', type: 'text' },
      { key: 'finance.defaultBudgetAlertThreshold', label: 'Default budget alert %', type: 'number' },
    ],
  },
  {
    // Subscriptions (2026-10-28): Finance's own keys, shown on a tab of their own.
    id: 'subscriptions',
    title: 'settings.subscriptions',
    note: 'settings.subscriptionsNote',
    settings: [
      {
        key: 'finance.subscriptionAutoBilling',
        label: 'Invoice each period automatically',
        type: 'boolean',
        hint: 'The hourly run issues each period’s invoice on its first day. Off: nothing is invoiced until it is back on, or by hand.',
      },
      {
        key: 'finance.subscriptionPaymentTermsDays',
        label: 'Days to pay a subscription invoice',
        type: 'number',
        hint: 'Its due date is this many days after it is issued (0–365).',
      },
      {
        key: 'finance.subscriptionGraceDays',
        label: 'Grace days before Past due',
        type: 'number',
        hint: 'Days after an invoice’s due date before its subscription is Past due (0–365). Nothing is cancelled for it, and billing goes on.',
      },
      { key: 'finance.subscriptionNumberPrefix', label: 'Subscription number prefix', type: 'text', hint: 'SUB-2026-0001 …' },
    ],
  },
  {
    id: 'payroll',
    title: 'settings.payroll',
    settings: [
      { key: 'payroll.salaryExpenseCategoryCode', label: 'Salary expense category code', type: 'text' },
      { key: 'payroll.standardMonthlyDays', label: 'Standard monthly days', type: 'number' },
      {
        key: 'payroll.autoCreateSalaryExpense',
        label: 'Create a salary expense when payroll is paid',
        type: 'boolean',
        hint: '§20.6 — created exactly once per period, never double counted.',
      },
    ],
  },
  {
    id: 'clients',
    title: 'clients.title',
    settings: [
      { key: 'clients.codePrefix', label: 'Client code prefix', type: 'text', hint: 'CL-0001, CL-0002 … (CR-001 §3).' },
      {
        key: 'clients.codePadding',
        label: 'Client code digits',
        type: 'number',
        hint: 'For the first code only. After it, a new client is offered the last code + 1 with as many digits: CL-009 → CL-010.',
      },
    ],
  },
];

export function SettingsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  // Sys admins own the settings; an accountant keeps the currency rates current,
  // which is their day-to-day work, and sees only that tab. The external systems
  // are the Finance admin's (2026-10-28).
  const tabs = [
    ...(can(P.FINANCE_SETTINGS_MANAGE) ? GROUPS.map((g) => ({ id: g.id, label: t(g.title) })) : []),
    ...(can(P.FINANCE_EXCHANGE_RATE_EDIT) ? [{ id: 'currencies', label: t('settings.currencies') }] : []),
    ...(can(P.EXTERNAL_SYSTEMS_MANAGE) ? [{ id: 'external-systems', label: t('settings.externalSystems') }] : []),
  ];
  // A link may open a tab: /settings?tab=external-systems.
  const asked = params.get('tab');
  const chosen = asked && tabs.some((x) => x.id === asked) ? asked : null;
  const setTab = (id: string) => setParams({ tab: id }, { replace: true });
  const tab = chosen ?? tabs[0]?.id ?? null;

  return (
    <>
      <PageHeader title={t('settings.title')} />
      {tab === null ? (
        <Card><EmptyState title={t('common.noResults')} hint="" /></Card>
      ) : (
        <>
          <Tabs active={tab} onChange={setTab} tabs={tabs} />
          <div className="mt-5">
            {tab === 'currencies' ? (
              <CurrenciesPanel />
            ) : tab === 'external-systems' ? (
              <ExternalSystemsPanel />
            ) : (
              <SettingsGroup groupId={tab} />
            )}
          </div>
        </>
      )}
    </>
  );
}

function SettingsGroup({ groupId }: { groupId: string }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const group = GROUPS.find((g) => g.id === groupId)!;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
  });

  const [draft, setDraft] = useState<Record<string, unknown>>({});

  useEffect(() => {
    // Reset the draft whenever the tab or the server values change.
    setDraft({});
  }, [groupId, data]);

  const save = useMutation({
    mutationFn: () => api.put('/settings', { values: draft }),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  // Read from the key's own group: the subscriptions' tab shows `finance.*` keys.
  const current = (key: string): unknown => (key in draft ? draft[key] : data?.[key.split('.')[0]]?.[key]);
  const dirty = Object.keys(draft).length > 0;

  return (
    <Card>
      <CardHeader
        title={t(group.title)}
        subtitle={group.note ? t(group.note) : undefined}
        action={
          <Button
            variant="primary"
            icon={<Save className="h-4 w-4" />}
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {t('common.save')}
          </Button>
        }
      />

      <div className="grid gap-5 sm:grid-cols-2">
        {group.settings.map((spec) => {
          const value = current(spec.key);
          const changed = spec.key in draft;

          return (
            <Field
              key={spec.key}
              label={
                <span className="flex items-center gap-2">
                  {spec.label}
                  {changed && <Badge tone="warning">unsaved</Badge>}
                </span>
              }
              hint={spec.hint ?? spec.key}
            >
              {spec.type === 'boolean' ? (
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.checked })}
                  label={value ? t('common.yes') : t('common.no')}
                />
              ) : spec.type === 'weekday' ? (
                <Select
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: Number(e.target.value) })}
                >
                  {DAY_NAMES[locale].map((day, index) => (
                    <option key={day} value={index}>{day}</option>
                  ))}
                  {/* A value saved before this was a dropdown may be out of range — show it
                      rather than silently displaying Sunday over the top of it. */}
                  {!DAY_NAMES[locale][Number(value)] && (
                    <option value={String(value ?? '')}>{String(value ?? '—')}</option>
                  )}
                </Select>
              ) : spec.type === 'select' ? (
                <Select
                  value={String(value ?? '')}
                  onChange={(e) => setDraft({ ...draft, [spec.key]: e.target.value })}
                >
                  {spec.options?.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </Select>
              ) : (
                <Input
                  type={spec.type === 'number' ? 'number' : 'text'}
                  value={String(value ?? '')}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      [spec.key]: spec.type === 'number' ? Number(e.target.value) : e.target.value,
                    })
                  }
                />
              )}
            </Field>
          );
        })}
      </div>
    </Card>
  );
}

interface CurrencyRow {
  code: string;
  name: string;
  nameAr: string | null;
  symbol: string | null;
  isActive: boolean;
  rateToBase: number | null;
  rateUpdatedAt: string | null;
  isBase: boolean;
}

type CurrencyEdit = { isActive?: boolean; rateToBase?: number | null };

const sameRate = (a: number | null | undefined, b: number | null | undefined) =>
  a == null || b == null ? a == null && b == null : Math.abs(a - b) < 1e-9;

/**
 * CR-001 §7 — the currencies we actually trade in, and what each is worth in the
 * base currency today. Switching one off removes it from every currency picker
 * without touching the records that already use it.
 *
 * The rate is a *suggestion*: it prefills a new invoice or payment and nothing
 * else. §27 — a payment keeps the rate captured with it, so editing this table
 * never moves money that has already been recorded.
 */
function CurrenciesPanel() {
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Record<string, CurrencyEdit>>({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['currencies', 'all'],
    queryFn: () => api.get<CurrencyRow[]>('/currencies?includeInactive=true'),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put('/currencies', {
        currencies: Object.entries(draft).map(([code, edit]) => ({ code, ...edit })),
      }),
    onSuccess: () => {
      toast.success(t('currencies.saved'));
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['currencies'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const rows = data ?? [];
  const base = rows.find((c) => c.isBase)?.code ?? 'EGP';

  const edit = (code: string, change: CurrencyEdit) =>
    setDraft((d) => ({ ...d, [code]: { ...d[code], ...change } }));

  const activeOf = (c: CurrencyRow) => draft[c.code]?.isActive ?? c.isActive;
  const rateOf = (c: CurrencyRow) =>
    draft[c.code] && 'rateToBase' in draft[c.code] ? draft[c.code].rateToBase : c.rateToBase;
  const changed = (c: CurrencyRow) => activeOf(c) !== c.isActive || !sameRate(rateOf(c), c.rateToBase);

  const inUse = rows.filter(activeOf).length;

  // The currencies we bill in come first, then the rest, alphabetical within
  // each. This deliberately reads the *saved* flag rather than the draft: with
  // 68 rows, sorting on the tick you just made would slide the row out from
  // under the pointer mid-edit. The list settles into its new order on save.
  const ordered = [...rows].sort((a, b) => {
    if (a.isBase !== b.isBase) return a.isBase ? -1 : 1;
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.code.localeCompare(b.code);
  });

  const needle = search.trim().toLowerCase();
  const visible = needle
    ? ordered.filter((c) =>
        [c.code, c.name, c.nameAr ?? ''].some((field) => field.toLowerCase().includes(needle)),
      )
    : ordered;

  return (
    <Card padded={false}>
      <CardHeader
        title={t('currencies.title')}
        subtitle={t('currencies.inUseCount', { count: inUse })}
        className="p-5 pb-0"
        action={
          <div className="flex items-center gap-2">
            {/* Every ISO currency ships active, so the first pass through this
                screen is 60-odd unticks. This makes it two clicks and a save. */}
            <Button
              variant="ghost"
              disabled={!rows.some((c) => !c.isBase && activeOf(c))}
              onClick={() =>
                setDraft((d) => {
                  const next = { ...d };
                  for (const c of rows) {
                    if (!c.isBase) next[c.code] = { ...next[c.code], isActive: false };
                  }
                  return next;
                })
              }
            >
              {t('currencies.switchAllOff')}
            </Button>
            <Button
              variant="primary"
              icon={<Save className="h-4 w-4" />}
              disabled={!rows.some(changed)}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </Button>
          </div>
        }
      />

      <div className="px-5">
        <p className="mb-4 text-body-sm text-muted">{t('currencies.note')}</p>
        <Input
          className="mb-4 max-w-sm"
          placeholder={t('currencies.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {visible.length ? (
        <Table>
          <thead>
            <tr>
              <Th>{t('currencies.currency')}</Th>
              <Th>{t('currencies.inUse')}</Th>
              <Th align="end">{t('currencies.rateTo', { base })}</Th>
              <Th align="end">{t('currencies.rateUpdated')}</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const rate = rateOf(c);
              return (
                <Tr key={c.code}>
                  <Td>
                    <span className="font-mono text-mono-sm">{c.code}</span>
                    <span className="ms-2 text-muted">{localized(c)}</span>
                    {c.isBase && <Badge className="ms-2" tone="info">{t('currencies.base')}</Badge>}
                    {changed(c) && <Badge className="ms-2" tone="warning">unsaved</Badge>}
                  </Td>
                  <Td>
                    <Checkbox
                      checked={activeOf(c)}
                      // The base currency is what everything reports in, so it
                      // can never be switched off.
                      disabled={c.isBase}
                      onChange={(e) => edit(c.code, { isActive: e.target.checked })}
                    />
                  </Td>
                  <Td align="end">
                    <Input
                      type="number"
                      step="0.0001"
                      min="0"
                      className="w-36 text-end tabular"
                      disabled={c.isBase}
                      placeholder={t('currencies.missingRate')}
                      title={t('currencies.rateHint', { code: c.code, base })}
                      value={rate == null ? '' : String(rate)}
                      onChange={(e) =>
                        edit(c.code, { rateToBase: e.target.value === '' ? null : Number(e.target.value) })
                      }
                    />
                  </Td>
                  <Td align="end" className="tabular text-muted">
                    {c.rateUpdatedAt ? formatDateTime(c.rateUpdatedAt, locale) : '—'}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <EmptyState title={t('currencies.noneMatch')} hint="" />
      )}
    </Card>
  );
}
